// useAudioLevel.js - Hook for detecting speaking via audio levels
import { useEffect, useState, useRef } from 'react';

export function useAudioLevel(stream, enabled = true) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const analyzerRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!stream || !enabled) {
      setIsSpeaking(false);
      setAudioLevel(0);
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyzer = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    
    analyzer.fftSize = 512;
    analyzer.smoothingTimeConstant = 0.8;
    source.connect(analyzer);
    analyzerRef.current = analyzer;

    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const SPEAKING_THRESHOLD = 15; // Adjust this (0-100) - lower = more sensitive
    const SILENCE_FRAMES = 8; // Frames of silence before "not speaking"
    let silenceCounter = 0;

    function checkAudioLevel() {
      analyzer.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      const sum = dataArray.reduce((acc, val) => acc + val, 0);
      const average = sum / bufferLength;
      const normalizedLevel = Math.min(100, (average / 255) * 100);
      
      setAudioLevel(normalizedLevel);

      if (normalizedLevel > SPEAKING_THRESHOLD) {
        setIsSpeaking(true);
        silenceCounter = 0;
      } else {
        silenceCounter++;
        if (silenceCounter > SILENCE_FRAMES) {
          setIsSpeaking(false);
        }
      }

      animationRef.current = requestAnimationFrame(checkAudioLevel);
    }

    checkAudioLevel();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (source) source.disconnect();
      if (audioContext.state !== 'closed') audioContext.close();
    };
  }, [stream, enabled]);

  return { isSpeaking, audioLevel };
}

// Usage in Meeting.js:
// const { isSpeaking: localSpeaking } = useAudioLevel(localStreamRef.current, micOn);
// const { isSpeaking: remoteSpeaking } = useAudioLevel(remoteRef.current?.srcObject);